/**
 * SIMPL branding for the Cognito-mode Amplify <Authenticator> (deployed test
 * envs without simpl_auth — dev/testone/testtwo/testthree/sadev/hsdev).
 *
 * Restores the pre-simpl_auth-migration login look: SIMPL logo header, themed
 * colors, "Email" placeholders, and friendly error copy. The components live
 * in features/login/ (still present) — this module just re-assembles the theme
 * / components / formFields the old ProtectedApp.tsx used before the migration
 * removed the Authenticator UI (commit e3eeb155).
 */
import { Theme } from "@aws-amplify/ui-react";
import { DefaultComponents } from "@aws-amplify/ui-react/dist/types/components/Authenticator/hooks/useCustomComponents/defaultComponents";
import { AuthFormFields } from "@aws-amplify/ui/dist/types/types/authenticator/form";
import { I18n } from "aws-amplify";

import { Header } from "../login/Header";
import { SignInHeader } from "../login/SignInHeader";
import { SignInFooter } from "../login/SignInFooter";
import { Footer } from "../login/Footer";
import { ResetPasswordHeader } from "../login/ResetPasswordHeader";
import { ResetPasswordFooter } from "../login/ResetPasswordFooter";
// NOTE: these two files have swapped export names (pre-existing) — import each
// by its actual export, matching the old ProtectedApp.tsx.
import { ConfirmResetPasswordHeader } from "../login/ConfirmResetPasswordFooter";
import { ConfirmResetPasswordFooter } from "../login/ConfirmResetPasswordHeader";
import { ConfirmSignUpHeader } from "../login/ConfirmSignUpHeader";
import { handleForgotPassword } from "../login/forgotPasswordUtils";
import { InvitationFormErrors } from "../UserInvitations/userInvitationUtils";
import { SIMPLColors } from "../../theme";
import "../login/styles.css";

I18n.putVocabulariesForLanguage("en", {
  "Incorrect username or password.":
    "Email address and password combination is not recognized. Try again.",
  "Password does not conform to policy: Password must have lowercase characters":
    InvitationFormErrors.PasswordRequirementsNotMet,
  "Password does not conform to policy: Password must have uppercase characters":
    InvitationFormErrors.PasswordRequirementsNotMet,
  "Password does not conform to policy: Password must have numeric characters":
    InvitationFormErrors.PasswordRequirementsNotMet,
  "Password does not conform to policy: Password must have symbol characters":
    InvitationFormErrors.PasswordRequirementsNotMet
});

export const cognitoAuthenticatorComponents: DefaultComponents = {
  Header,
  SignIn: {
    Header: SignInHeader,
    Footer: SignInFooter
  },
  Footer: Footer,
  ResetPassword: {
    Header: ResetPasswordHeader,
    Footer: ResetPasswordFooter
  },
  ConfirmResetPassword: {
    Header: ConfirmResetPasswordHeader,
    Footer: ConfirmResetPasswordFooter
  },
  ConfirmSignUp: {
    Header: ConfirmSignUpHeader
  }
};

export const cognitoFormFields: AuthFormFields = {
  signIn: {
    username: {
      placeholder: "Email",
      label: "",
      isRequired: true,
      type: "email"
    },
    password: { placeholder: "Password", label: "", isRequired: true }
  },
  resetPassword: {
    username: {
      placeholder: "Enter your email",
      label: "",
      isRequired: true,
      type: "email"
    }
  },
  confirmResetPassword: {
    confirmation_code: { placeholder: "Code", label: "", isRequired: true },
    password: { placeholder: "New Password", label: "", isRequired: true },
    confirm_password: {
      placeholder: "Confirm Password",
      label: "",
      isRequired: true
    }
  }
};

export const cognitoAuthTheme: Theme = {
  name: "Auth Theme",
  tokens: {
    fonts: {
      default: {
        variable: { value: "Roboto, sans-serif" },
        static: { value: "Roboto, sans-serif" }
      }
    },
    colors: {
      font: {
        interactive: { value: SIMPLColors.Primary },
        active: { value: SIMPLColors.Secondary },
        hover: { value: SIMPLColors.Secondary },
        focus: { value: SIMPLColors.Secondary }
      }
    },
    components: {
      button: {
        primary: {
          backgroundColor: { value: SIMPLColors.Primary },
          _hover: { backgroundColor: SIMPLColors.Secondary },
          _focus: { backgroundColor: SIMPLColors.Secondary },
          _active: { backgroundColor: SIMPLColors.Secondary }
        }
      }
    }
  }
};

export const cognitoAuthServices = {
  async handleForgotPassword(username: string) {
    return await handleForgotPassword(username);
  }
};
